import { useEffect, useState, type MouseEvent } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ContentsEntry } from './articleContents';

// A heading counts as the current section once it has scrolled up past this line.
const CURRENT_LINE_PX = 120;

function jumpTo(e: MouseEvent<HTMLAnchorElement>, id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  e.preventDefault();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  // Keeps the section linkable without a router navigation, which would re-render the page.
  window.history.replaceState(window.history.state, '', `#${id}`);
}

function useCurrentSection(entries: ContentsEntry[]): string | undefined {
  const [current, setCurrent] = useState<string | undefined>(entries[0]?.id);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      let id = entries[0]?.id;
      entries.forEach(entry => {
        const el = document.getElementById(entry.id);
        if (el && el.getBoundingClientRect().top < CURRENT_LINE_PX) id = entry.id;
      });
      setCurrent(id);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [entries]);
  return current;
}

function EntryLink({ entry, current }: { entry: ContentsEntry; current?: boolean }) {
  return (
    <a
      href={`#${entry.id}`}
      onClick={e => jumpTo(e, entry.id)}
      aria-current={current ? 'location' : undefined}
      className={cn(
        'block rounded-etn-menu px-2.5 py-1.5 text-etn-control leading-snug text-pretty transition-colors',
        entry.depth === 3 && 'pl-6 text-etn-control-sm',
        // The current section is tinted, never marked with a side border.
        current
          ? 'bg-etn-accent-tint font-semibold text-etn-accent-text-active'
          : 'text-etn-text-label hover:bg-etn-control-subtle hover:text-etn-text'
      )}
    >
      {entry.text}
    </a>
  );
}

/** The sticky "On this page" rail beside the article, from lg up. */
export function KbArticleContentsRail({ entries }: { entries: ContentsEntry[] }) {
  const current = useCurrentSection(entries);
  return (
    <nav aria-label="On this page" className="sticky top-6 hidden max-h-[calc(100dvh-3rem)] overflow-y-auto lg:block">
      <p className="mb-2 px-2.5 text-etn-micro font-semibold uppercase tracking-wider text-etn-text-soft">On this page</p>
      <ol className="space-y-0.5">
        {entries.map(entry => (
          <li key={entry.id}>
            <EntryLink entry={entry} current={entry.id === current} />
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Below lg there is no room for the rail, so the same list sits above the article, collapsed. */
export function KbArticleContentsInline({ entries }: { entries: ContentsEntry[] }) {
  return (
    <details className="group mb-6 rounded-etn-card border border-etn-border-hairline bg-etn-surface lg:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-2.5 text-etn-control font-semibold text-etn-text">
        On this page
        <ChevronDown className="h-4 w-4 text-etn-text-soft transition-transform group-open:rotate-180" aria-hidden />
      </summary>
      <ol className="space-y-0.5 px-1.5 pb-2">
        {entries.map(entry => (
          <li key={entry.id}>
            <EntryLink entry={entry} />
          </li>
        ))}
      </ol>
    </details>
  );
}
